import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Group,
  Text,
  TextInput,
  Select,
  Grid,
  Col,
  Avatar,
  Stack,
  Badge,
} from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useAtom } from "jotai";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import MGAAPI from "../../api/index";
import SEConfirmModal from "../common/SEConfirmModal";
import {
  RawBusinessTypeList,
  mgaRegex,
  normalizeSpaces,
  convertPictureToBlobUrl,
  dataUriToFile,
  validateProfilePicture,
} from "../../constants/helper";
import formatToUTCWithGMT from "../../utils/dateHelper";
import displayError from "../../constants/utils";
import { getErrorMessage } from "../../utils/errorUtils";

const apiSession = new MGAAPI();

const EMPTY_FIELD_ERRORS = { firstName: "", lastName: "", phoneNumber: "", businessType: "", preferredLanguage: "", employer: "" };
const EMPTY_FORM = { firstName: "", lastName: "", phoneNumber: "", businessType: "", preferredLanguage: "en", employer: "", picture: "" };

const brokenImgSvg = (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <path d="M3 16l5-5 4 4 5-6 4 5" />
    <circle cx="8" cy="8" r="1" />
  </svg>
);

const ProfileModal = ({ opened, onClose }) => {
  const { i18n, t } = useTranslation();
  const [userScope, setUserScope] = useAtom(userScopeAtom);

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [profileForm, setProfileForm] = useState(EMPTY_FORM);
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);
  const [profileUpdateError, setProfileUpdateError] = useState("");
  const [profileFieldErrors, setProfileFieldErrors] = useState(EMPTY_FIELD_ERRORS);
  const [isEditMode, setIsEditMode] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!opened) return;
    setProfileLoading(true);
    setProfileError("");
    setProfileUpdateError("");
    setProfileFieldErrors(EMPTY_FIELD_ERRORS);
    setIsEditMode(false);
    setProfilePicturePreview(null);
    apiSession
      .getUserProfile()
      .then((res) => {
        setProfileData(res || {});
        const convertedPicture = convertPictureToBlobUrl(res?.picture);
        setProfileForm({
          firstName: res?.firstName || "",
          lastName: res?.lastName || "",
          phoneNumber: res?.phoneNumber || "",
          businessType: res?.businessType || "",
          preferredLanguage: res?.preferredLanguage || "en",
          employer: res?.employer || "",
          picture: convertedPicture,
        });
      })
      .catch((e) => {
        setProfileError(e?.message || t("profile.error.loadFailed"));
        setProfileData(null);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  }, [opened]);

  const resetForm = (data = profileData) => {
    setProfileForm({
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      phoneNumber: data?.phoneNumber || "",
      businessType: data?.businessType || "",
      preferredLanguage: data?.preferredLanguage || "en",
      employer: data?.employer || "",
      picture: convertPictureToBlobUrl(data?.picture),
    });
    if (profilePicturePreview) URL.revokeObjectURL(profilePicturePreview);
    setProfilePicturePreview(null);
    setProfileUpdateError("");
    setProfileFieldErrors(EMPTY_FIELD_ERRORS);
  };

  const handleClose = () => {
    resetForm();
    setIsEditMode(false);
    onClose();
  };

  const handleEditCancel = () => {
    resetForm();
    setIsEditMode(false);
  };

  const updateForm = (updater) => {
    setProfileUpdateError("");
    setProfileFieldErrors(EMPTY_FIELD_ERRORS);
    setProfileForm(updater);
  };

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validateProfilePicture(file);
    if (error) {
      setProfileUpdateError(error);
      return;
    }
    setProfileUpdateError("");
    setProfileForm((f) => ({ ...f, picture: file }));
    setProfilePicturePreview(URL.createObjectURL(file));
  };

  const handleProfileUpdate = async () => {
    const firstName = profileForm.firstName?.trim() ?? "";
    const lastName = profileForm.lastName?.trim() ?? "";
    const employer = profileForm.employer?.trim() ?? "";
    const businessType = profileForm.businessType || profileData?.businessType || "";
    const phoneNumber = profileForm.phoneNumber?.trim() ?? "";

    const nameRules = [
      { field: "firstName", value: firstName, required: "profile.validation.firstNameRequired", invalid: "profile.validation.invalidFirstName" },
      { field: "lastName",  value: lastName,  required: "profile.validation.lastNameRequired",  invalid: "profile.validation.invalidLastName"  },
      { field: "employer",  value: employer,  required: "profile.validation.employerRequired",  invalid: "profile.validation.invalidEmployer"  },
    ];
    const fieldErrors = {};
    nameRules.forEach(({ field, value, required, invalid }) => {
      if (!value) fieldErrors[field] = t(required);
      else if (!mgaRegex.namePattern.test(value)) fieldErrors[field] = t(invalid);
    });
    if (!businessType) fieldErrors.businessType = t("profile.validation.businessTypeRequired");
    if (!phoneNumber) fieldErrors.phoneNumber = t("profile.validation.phoneNumberRequired");
    else if (!mgaRegex.phoneNumberRegex.test(phoneNumber)) fieldErrors.phoneNumber = t("profile.validation.invalidPhoneNumber");
    if (Object.keys(fieldErrors).length > 0) {
      setProfileFieldErrors((prev) => ({ ...prev, ...fieldErrors }));
      return;
    }

    setProfileUpdateLoading(true);
    setProfileUpdateError("");
    try {
      const userIdentifier = userScope?.email || userScope?.id || "";
      const payload = {
        firstName: profileForm.firstName?.trim() ?? "",
        lastName: profileForm.lastName?.trim() ?? "",
        phoneNumber: profileForm.phoneNumber?.trim() ?? "",
        businessType: profileForm.businessType || profileData?.businessType || "",
        preferredLanguage:
          profileForm.preferredLanguage || profileData?.preferredLanguage || "en",
        employer: profileForm.employer?.trim() ?? "",
        picture: (() => {
          const pic = profileForm.picture;
          if (pic instanceof File) return pic;
          if (!pic) return null;
          if (typeof pic === "string" && pic.startsWith("data:") && pic.includes(";base64,")) {
            return dataUriToFile(pic) ?? null;
          }
          return pic || profileData?.picture || null;
        })(),
      };
      const updated = await apiSession.updateUserProfile(userIdentifier, payload);
      const preferredLanguage = (updated?.preferredLanguage || payload.preferredLanguage)
        ?.slice(0, 2)
        ?.toLowerCase();
      if (preferredLanguage === "en" || preferredLanguage === "fr") {
        await i18n.changeLanguage(preferredLanguage);
        localStorage.setItem("i18nextLng", preferredLanguage);
      }
      if (profilePicturePreview) URL.revokeObjectURL(profilePicturePreview);
      setProfilePicturePreview(null);
      setIsEditMode(false);
      setProfileData(updated || {});
      const convertedPicture = convertPictureToBlobUrl(updated?.picture);
      setProfileForm({
        firstName: updated?.firstName || "",
        lastName: updated?.lastName || "",
        phoneNumber: updated?.phoneNumber || "",
        businessType: updated?.businessType || "",
        preferredLanguage: updated?.preferredLanguage || "en",
        employer: updated?.employer || "",
        picture: convertedPicture,
      });

      const rawScope = localStorage.getItem("scope");
      if (rawScope) {
          const parsedScope = JSON.parse(rawScope);
          const concatenatedName = (updated?.firstName || "") + " " + (updated?.lastName || "");
          const updatedScope = {
            ...parsedScope,
            name: concatenatedName,
          };
          localStorage.setItem("scope", JSON.stringify(updatedScope));
          setUserScope(updatedScope);
      }

      displayError("success", t("profile.success.updated"));
    } catch (e) {
      const errMsg = getErrorMessage(e) || t("profile.error.updateFailed");
      setProfileUpdateError(errMsg);
      displayError("error", errMsg);
    } finally {
      setProfileUpdateLoading(false);
    }
  };

  const renderContent = () => {
    if (profileLoading) {
      return (
        <Group position="center" p="md">
          <se-loading loading={profileLoading} />
        </Group>
      );
    }
    if (profileError) {
      return (
        <Text color="red" size="sm">
          {profileError}
        </Text>
      );
    }
    if (!profileData) {
      return (
        <Text size="sm" color="dimmed" italic>
          {t("profile.noData")}
        </Text>
      );
    }

    const statusText = profileData.status === "A" ? "Active" : "Inactive";
    let updatedDisplay = formatToUTCWithGMT(profileData?.updatedAt) || "—";
    const match = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(updatedDisplay);
    if (match) {
      const [, y, m, d, time] = match;
      const dateObj = new Date(`${y}-${m}-${d}T${time}Z`);
      if (!isNaN(dateObj.getTime())) {
        const opts = { month: "short", day: "2-digit", year: "numeric" };
        updatedDisplay = `${dateObj.toLocaleDateString(undefined, opts)} ${time}`;
      }
    }

    return (
      <Stack spacing="md">
        <Grid gutter="md" align="flex-start">
          <Col span={8}>
            <Stack spacing="sm">
              <div>
                <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.email")}</Text>
                <Text size="sm" sx={{ wordBreak: "break-all" }}>{profileData.email || "—"}</Text>
              </div>
              <Grid gutter="sm">
                <Col span={12}>
                  {isEditMode ? (
                    <TextInput
                      label={t("profile.label.firstName")}
                      value={profileForm.firstName}
                      error={profileFieldErrors.firstName}
                      onChange={(e) => updateForm((f) => ({ ...f, firstName: normalizeSpaces(e.target.value) }))}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.firstName")}</Text>
                      <Text weight={600}>{profileForm.firstName || "—"}</Text>
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {isEditMode ? (
                    <TextInput
                      label={t("profile.label.lastName")}
                      value={profileForm.lastName}
                      error={profileFieldErrors.lastName}
                      onChange={(e) => updateForm((f) => ({ ...f, lastName: normalizeSpaces(e.target.value) }))}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.lastName")}</Text>
                      <Text weight={600}>{profileForm.lastName || "—"}</Text>
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {isEditMode ? (
                    <TextInput
                      label={t("profile.label.phoneNumber")}
                      value={profileForm.phoneNumber}
                      error={profileFieldErrors.phoneNumber}
                      onChange={(e) => updateForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.phoneNumber")}</Text>
                      <Text size="sm">{profileForm.phoneNumber || "—"}</Text>
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {isEditMode ? (
                    <Select
                      label={t("profile.label.businessType")}
                      value={profileForm.businessType}
                      error={profileFieldErrors.businessType}
                      onChange={(val) => updateForm((f) => ({ ...f, businessType: val || "" }))}
                      data={RawBusinessTypeList?.businessTypeList?.map((businessData) => ({
                        value: businessData,
                        label: businessData,
                      }))}
                      withinPortal
                      searchable
                      rightSection={<IconCaretDownFilled size={18} />}
                      rightSectionProps={{ style: { pointerEvents: "none" } }}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.businessType")}</Text>
                      <Text size="sm">{profileForm.businessType || "—"}</Text>
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {isEditMode ? (
                    <Select
                      label={t("profile.label.preferredLanguage")}
                      value={profileForm.preferredLanguage}
                      error={profileFieldErrors.preferredLanguage}
                      onChange={(val) => updateForm((f) => ({ ...f, preferredLanguage: val || "" }))}
                      data={[
                        { value: "en", label: "English" },
                        { value: "fr", label: "French" },
                      ]}
                      withinPortal
                      rightSection={<IconCaretDownFilled size={18} />}
                      rightSectionProps={{ style: { pointerEvents: "none" } }}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.preferredLanguage")}</Text>
                      <Text size="sm">{profileForm.preferredLanguage === "fr" ? "French" : "English"}</Text>
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {isEditMode ? (
                    <TextInput
                      label={t("profile.label.employer")}
                      value={profileForm.employer}
                      error={profileFieldErrors.employer}
                      onChange={(e) => updateForm((f) => ({ ...f, employer: normalizeSpaces(e.target.value) }))}
                    />
                  ) : (
                    <div>
                      <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.employer")}</Text>
                      <Text size="sm">{profileForm.employer || "—"}</Text>
                    </div>
                  )}
                </Col>
                {!isEditMode && (
                  <>
                    <Col span={12}>
                      <div>
                        <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.updatedAt")}</Text>
                        <Text size="sm">{updatedDisplay}</Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text size="xs" transform="uppercase" weight={600} color="dimmed">{t("profile.label.status")}</Text>
                        <Badge color={statusText === "Active" ? "green" : "red"} variant="light">
                          {statusText}
                        </Badge>
                      </div>
                    </Col>
                  </>
                )}
              </Grid>
            </Stack>
          </Col>
          <Col span={4}>
            <Stack align="center" justify="center" spacing={6} sx={{ height: "100%" }}>
              {!isEditMode && (
                <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                  <se-button aria-label="profile-edit" option="icon-only" icon="action_editor" color="primary" onClick={() => setIsEditMode(true)}>
                    {t("profile.edit")}
                  </se-button>
                </div>
              )}
              <div style={{ position: "relative", display: "inline-block" }}>
                <div
                  style={{ position: "relative", cursor: isEditMode ? "pointer" : "default" }}
                  title={isEditMode ? t("profile.upload") || "Click to upload picture" : ""}
                  onClick={() => isEditMode && fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (!isEditMode) return;
                    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                      e.preventDefault();
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }
                  }}
                  role="button"
                >
                  {profilePicturePreview ? (
                    <Avatar src={profilePicturePreview} size={200} radius="md" alt="New profile picture" />
                  ) : profileForm.picture && typeof profileForm.picture === "string" ? (
                    <Avatar
                      src={profileForm.picture}
                      size={200}
                      radius="md"
                      alt={[profileData.firstName, profileData.lastName].filter(Boolean).join(" ") || "Profile"}
                    />
                  ) : (
                    <Avatar size={200} radius="md" color="gray">{brokenImgSvg}</Avatar>
                  )}
                  {isEditMode && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        fontSize: 10,
                        textAlign: "center",
                        borderRadius: "0 0 6px 6px",
                        padding: "2px 0",
                        pointerEvents: "none",
                      }}
                    >
                      {t("profile.upload")}
                    </div>
                  )}
                </div>
                {isEditMode && (profilePicturePreview || (profileForm.picture && typeof profileForm.picture === "string")) && (
                  <div
                    title={t("profile.removePicture") || "Remove picture"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (profilePicturePreview) URL.revokeObjectURL(profilePicturePreview);
                      setProfilePicturePreview(null);
                      setProfileUpdateError("");
                      setProfileForm((f) => ({ ...f, picture: "" }));
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (profilePicturePreview) URL.revokeObjectURL(profilePicturePreview);
                        setProfilePicturePreview(null);
                        setProfileUpdateError("");
                        setProfileForm((f) => ({ ...f, picture: "" }));
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }
                    }}
                    role="button"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#e03131",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      lineHeight: 1,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                      userSelect: "none",
                    }}
                  >
                    ×
                  </div>
                )}
              </div>
              {isEditMode && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  style={{ display: "none" }}
                  onChange={handlePictureChange}
                />
              )}
            </Stack>
          </Col>
        </Grid>
        {profileUpdateError && (
          <Text color="red" size="sm">{profileUpdateError}</Text>
        )}
      </Stack>
    );
  };

  return (
    <>
      <se-snackbar id="snackbar2" duration="3000" icon="information_stroke"></se-snackbar>
      <SEConfirmModal
        opened={opened}
        onClose={isEditMode ? handleEditCancel : handleClose}
        onConfirm={handleProfileUpdate}
        onlyOk={!isEditMode}
        header={i18n.t("profile.title")}
        confirmLabel={isEditMode ? i18n.t("profile.save") : i18n.t("profile.ok")}
        confirmLoadingLabel={i18n.t("profile.updating")}
        cancelLabel={i18n.t("common.cancel") || "Cancel"}
        confirmLoading={profileUpdateLoading}
        disableOutsideClickClose={true}
        size="lg"
        message={renderContent()}
        customStyles={{
          content: {
            flex: 'auto !important'
          },
        }}
      />
    </>
  );
};

export default ProfileModal;
